import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../../core/auth/auth.service';
import { InvitationService } from '../../../core/entity/invitation.service';
import { InvitationTokenInfo } from '../../../core/entity/invitation.types';
import { AiConsentCheckboxComponent } from '../../../shared/ai-consent/ai-consent-checkbox/ai-consent-checkbox';
import { IconComponent } from '../../../shared/icon/icon';

function passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as string;
  if (!value) return null;
  if (value.length < 8) return { minLength: true };
  if (!/[A-Z]/.test(value)) return { noUppercase: true };
  if (!/\d/.test(value)) return { noNumber: true };
  return null;
}

type PageState = 'loading' | 'ready' | 'invalid' | 'gone';

@Component({
  selector: 'app-accept-invitation',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    IconComponent,
    AiConsentCheckboxComponent,
  ],
  templateUrl: './accept-invitation.html',
  styleUrl: './accept-invitation.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcceptInvitationComponent implements OnInit {
  private readonly invitations = inject(InvitationService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  private token = '';

  protected readonly pageState = signal<PageState>('loading');
  protected readonly info = signal<InvitationTokenInfo | null>(null);
  protected readonly isAccepting = signal(false);
  protected readonly serverError = signal<string | null>(null);
  protected readonly showPassword = signal(false);

  protected readonly form = this.fb.group({
    firstName: ['', [Validators.required, Validators.maxLength(150)]],
    lastName: ['', [Validators.required, Validators.maxLength(150)]],
    newPassword: ['', [Validators.required, passwordStrengthValidator]],
    // POPIA s72 — accepting with a new password IS this person's sign-up, so
    // the once-only consent to AI-assisted assessment is asked here, as its
    // own question. An existing account answered it at its own sign-up.
    // Not required (Constitution §A6.7): left unticked, the account is still
    // created and the application screen puts the question again.
    aiConsent: [false],
  });

  protected onAiConsentChange(checked: boolean): void {
    this.form.controls.aiConsent.setValue(checked);
  }

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get('token');
    if (!token) {
      this.pageState.set('invalid');
      return;
    }
    this.token = token;
    this.invitations.tokenInfo(token).subscribe({
      next: (info) => {
        this.info.set(info);
        this.pageState.set('ready');
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.pageState.set(err.status === 410 ? 'gone' : 'invalid');
        this.cdr.markForCheck();
      },
    });
  }

  protected togglePassword(): void {
    this.showPassword.update((v) => !v);
  }

  protected accept(): void {
    const needsPassword = this.info()?.requires_password ?? false;
    if (needsPassword) {
      this.form.markAllAsTouched();
      if (this.form.invalid) return;
    }
    if (this.isAccepting()) return;

    this.serverError.set(null);
    this.isAccepting.set(true);

    const v = this.form.getRawValue();
    const payload = needsPassword
      ? {
          token: this.token,
          first_name: v.firstName!,
          last_name: v.lastName!,
          new_password: v.newPassword!,
          ai_processing_consent: v.aiConsent!,
        }
      : { token: this.token };

    this.invitations.accept(payload).subscribe({
      next: (user) => {
        this.auth.setUser(user);
        void this.router.navigate(['/dashboard']);
      },
      error: (err: HttpErrorResponse) => {
        this.isAccepting.set(false);
        if (err.status === 410) {
          this.pageState.set('gone');
        } else if (err.status === 400 && err.error?.detail?.new_password) {
          this.form.controls.newPassword.setErrors({
            serverError: (err.error.detail.new_password as string[])[0],
          });
        } else {
          this.serverError.set('Could not accept the invitation. Please try again.');
        }
        this.cdr.markForCheck();
      },
    });
  }
}
